import { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getDeviceContacts } from '../../../api/connections';
import type { DeviceContact } from '../../../types/connections';

interface MainSchemaDeviceNodeData {
  deviceName: string;
  deviceType: string;
  deviceTypeId?: number;
  specName: string;
  description: string;
  deviceInstanceId: number;
  specificationId?: number;
  contacts: DeviceContact[];
  powerConsumption?: number; // Power consumption in watts
  manufacturer?: string;
  model?: string;
  knxAddress?: string; // KNX address for KNX devices
  onRemoveDevice?: (layoutId: number) => void;
  onEditInstance?: (layoutId: number, deviceInstanceId: number) => void;
  layoutId: number;
  isReserve?: boolean; // true если элемент резервный
  schemaType?: string; // Legacy: Тип схемы для правильной загрузки контактов
  schemaId?: number; // ID конкретной схемы для фильтрации контактов
      // isConnecting?: boolean; - TEMPORARILY COMMENTED
}

interface MainSchemaDeviceNodeProps {
  data: MainSchemaDeviceNodeData;
}

/**
 * MAIN SCHEMA: Specialized node component for main electrical schemas
 * Features:
 * - Full contact visualization (TOP/BOTTOM for cables, TOP for devices)
 * - Power specifications display
 * - Color-coded wire connections
 * - Electrical device symbols with ratings
 */
export default function MainSchemaDeviceNode({ data }: MainSchemaDeviceNodeProps) {
  const {
    deviceName,
    deviceType,
    deviceTypeId,
    specName,
    description,
    deviceInstanceId,
    contacts: initialContacts = [],
    powerConsumption,
    manufacturer,
    model,
    knxAddress,
    isReserve = false,
    // isConnecting = false - TEMPORARILY COMMENTED
  } = data;

  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [loading, setLoading] = useState(false);
  const disableAutoLoadContacts = Boolean((data as any).disableAutoLoadContacts);

  const isCable = deviceType === 'cable' || deviceType === 'Кабель';

  // Color schemes based on reserve status
  const cableColors = {
    border: isReserve ? 'border-gray-500' : 'border-green-500',
    bg: isReserve ? 'bg-gray-50' : 'bg-green-50',
    bgSecondary: isReserve ? 'bg-gray-100' : 'bg-green-100',
    borderSecondary: isReserve ? 'border-gray-200' : 'border-green-200',
    text: isReserve ? 'text-gray-700' : 'text-green-700',
    textPrimary: isReserve ? 'text-gray-800' : 'text-green-800',
    handleBg: isReserve ? 'bg-gray-100' : 'bg-green-100',
    handleBorder: isReserve ? 'border-gray-500' : 'border-green-500'
  };

  const deviceColors = {
    border: isReserve ? 'border-gray-600' : 'border-blue-600',
    bg: isReserve ? 'bg-gray-50' : 'bg-blue-50',
    bgSecondary: isReserve ? 'bg-gray-100' : 'bg-blue-100',
    borderSecondary: isReserve ? 'border-gray-200' : 'border-blue-200',
    text: isReserve ? 'text-gray-700' : 'text-blue-700',
    textPrimary: isReserve ? 'text-gray-800' : 'text-blue-800',
    textSecondary: isReserve ? 'text-gray-600' : 'text-blue-600',
    handleBg: isReserve ? 'bg-gray-100' : 'bg-blue-100',
    handleBorder: isReserve ? 'border-gray-500' : 'border-blue-500'
  };

  // Sync contacts state with initialContacts prop for devices - ALWAYS update when initialContacts change
  useEffect(() => {
    if (initialContacts && initialContacts.length > 0) {
      setContacts(initialContacts);
    }
  }, [initialContacts, deviceName]);

  // Load cable contacts asynchronously for cable instances
  useEffect(() => {
    if (isCable && deviceInstanceId && !disableAutoLoadContacts) {
      const loadCableContacts = async () => {
        try {
          setLoading(true);
          // Используем schemaId (приоритет) или schemaType (legacy)
          const allCableContacts = data.specificationId 
            ? await getDeviceContacts(data.specificationId, undefined, data.schemaId) 
            : [];
          const cableContacts = allCableContacts;
          setContacts(cableContacts);
        } catch (error) {
          console.error('Error loading cable contacts:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCableContacts();
    }
  }, [isCable, deviceInstanceId, deviceName, disableAutoLoadContacts]);

  // Load device contacts asynchronously for non-cable devices
  // This ensures contacts are always loaded, even if initialContacts is empty
  useEffect(() => {
    if (!isCable && deviceInstanceId && data.specificationId && !disableAutoLoadContacts) {
      const loadDeviceContacts = async () => {
        try {
          // Always load fresh contacts to ensure we have all positions (including left/right)
          // Используем schemaId (приоритет) или schemaType (legacy)
          const specId = data.specificationId!; // Safe because we check above
          const allDeviceContacts = await getDeviceContacts(specId, undefined, data.schemaId);
          // Update contacts if we got any, or if initialContacts is empty
          if (allDeviceContacts && allDeviceContacts.length > 0) {
            setContacts(allDeviceContacts);
          } else if (!initialContacts || initialContacts.length === 0) {
            // Only set empty if we have no initial contacts
            setContacts([]);
          }
        } catch (error) {
          console.error('Error loading device contacts:', error);
          // Fallback to initialContacts if load fails
          if (initialContacts && initialContacts.length > 0) {
            setContacts(initialContacts);
          }
        }
      };

      loadDeviceContacts();
    }
  }, [isCable, deviceInstanceId, data.specificationId, data.schemaType, initialContacts, disableAutoLoadContacts]);

  // MAIN SCHEMA: Cable rendering with full wire visualization
  if (isCable) {
    // Filter and sort contacts by wire numbers for proper visualization
    const topContacts = contacts
      .filter(c => String(c.contact_number).includes('-TOP'))
      .sort((a, b) => {
        const numA = parseInt(String(a.contact_number).split('-')[0]);
        const numB = parseInt(String(b.contact_number).split('-')[0]);
        return numA - numB;
      });

    const bottomContacts = contacts
      .filter(c => String(c.contact_number).includes('-BOTTOM'))
      .sort((a, b) => {
        const numA = parseInt(String(a.contact_number).split('-')[0]);
        const numB = parseInt(String(b.contact_number).split('-')[0]);
        return numA - numB;
      });

    const contactsCount = topContacts.length;
    const cableWidth = contactsCount <= 4 ? 90 : Math.max(90, contactsCount * 18 + 20);

    if (loading) {
      return (
        <div className={`bg-white border-2 ${cableColors.border} rounded shadow-lg p-2`} style={{ width: cableWidth, minHeight: '120px' }}>
          <div className="text-center text-gray-500 text-xs">Loading cable...</div>
        </div>
      );
    }

    return (
      <div 
        className={`bg-white border-2 ${cableColors.border} rounded shadow-lg select-none`}
        style={{ width: cableWidth, minHeight: '120px' }}
      >
        {/* TOP contacts with handles - только TARGET для кабелей сверху */}
        <div className={`flex justify-around items-center px-1 py-1 border-b ${cableColors.borderSecondary}`}>
          {topContacts.map((contact) => (
            <div key={`${contact.contact_number}-top`} className="flex flex-col items-center relative">
              <div className={`w-3 h-3 rounded-full border-2 ${cableColors.handleBorder} ${cableColors.handleBg} relative`}>
                <Handle
                  type="target"
                  position={Position.Top}
                  id={`${contact.id}-target`}
                  className="schema-target-handle !w-px !h-px !bg-white !border-0"
                  // Center the invisible handle inside the visual dot to improve edge anchoring
                  style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Wire colors section */}
        <div className={`px-2 py-1 border-b ${cableColors.borderSecondary} ${cableColors.bg}`}>
          <div className="flex justify-around" style={{ fontSize: '8px' }}>
            {topContacts.map((contact) => (
              <div key={`color-${contact.contact_number}`} className={`text-center font-bold ${cableColors.text}`}>
                {contact.color_code || String(contact.contact_number).replace('-TOP', '')}
              </div>
            ))}
          </div>
        </div>

        {/* Position label */}
        <div className={`px-2 py-1 text-center border-b ${cableColors.borderSecondary}`}>
          <div className={`font-bold ${cableColors.textPrimary}`} style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {deviceName}
          </div>
          {/* Cable specification (название родителя) - под позиционным обозначением */}
          <div className={`font-bold ${cableColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all', marginTop: '2px' }}>
            {specName}
          </div>
        </div>

        {/* Wire colors section (bottom) - same as top */}
        <div className={`px-2 py-1 border-b ${cableColors.borderSecondary} ${cableColors.bg}`}>
          <div className="flex justify-around" style={{ fontSize: '8px' }}>
            {bottomContacts.map((contact) => (
              <div key={`color-bottom-${contact.contact_number}`} className={`text-center font-bold ${cableColors.text}`}>
                {contact.color_code || String(contact.contact_number).replace('-BOTTOM', '')}
              </div>
            ))}
          </div>
        </div>

        {/* BOTTOM contacts with handles - только SOURCE для кабелей снизу */}
        <div className="flex justify-around items-center px-1 py-1">
          {bottomContacts.map((contact) => (
            <div key={`${contact.contact_number}-bottom`} className="flex flex-col items-center relative">
              <div className={`w-3 h-3 rounded-full border-2 ${cableColors.handleBorder} ${cableColors.handleBg} relative`}>
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={`${contact.id}-source`}
                  className="schema-source-handle !w-px !h-px !bg-white !border-0"
                  style={{ 
                    bottom: '0px', 
                    transform: 'translateX(-50%)'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // MAIN SCHEMA: Device rendering with electrical specifications
  // Separate contacts by position: top, bottom, left, right
  const topContacts = contacts
    .filter(c => c.contact_position === 'top')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const bottomContacts = contacts
    .filter(c => c.contact_position === 'bottom')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const leftContacts = contacts
    .filter(c => c.contact_position === 'left')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const rightContacts = contacts
    .filter(c => c.contact_position === 'right')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const contactsCount = Math.max(topContacts.length, bottomContacts.length);
  const sideContactsCount = Math.max(leftContacts.length, rightContacts.length);
  const sideContactsHeight = sideContactsCount > 0 ? sideContactsCount * 22 + 10 : 0; // ~22px per contact + padding
  const deviceWidth = contactsCount <= 4 ? 80 : Math.max(80, contactsCount * 18 + 20);
  const sidePanelWidth = 40; // Width for left/right contact panels (increased for side labels)

  if (loading) {
    return (
      <div className={`bg-white border-2 ${deviceColors.border} rounded shadow-lg p-2`} style={{ width: deviceWidth, minHeight: '85px' }}>
        <div className="text-center text-gray-500 text-xs">Loading device...</div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white border-2 ${deviceColors.border} rounded shadow-lg select-none flex`}
      style={{ 
        width: deviceWidth + (leftContacts.length > 0 ? sidePanelWidth : 0) + (rightContacts.length > 0 ? sidePanelWidth : 0),
        minHeight: Math.max(85, sideContactsHeight || 85)
      }}
    >
      {/* LEFT contacts panel */}
      {leftContacts.length > 0 && (
        <div className="flex flex-col justify-center items-start border-r border-gray-200 py-1 px-1" style={{ width: sidePanelWidth, minHeight: '100%' }}>
          {leftContacts.map((contact) => (
            <div key={contact.id} className="flex items-center relative py-0.5 w-full">
              <div className={`w-3 h-3 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative flex-shrink-0`}>
                {/* Target handle для входящих соединений */}
                <Handle
                  type="target"
                  position={Position.Left}
                  id={`${contact.id}-target`}
                  className="schema-target-handle !w-px !h-px !bg-white !border-0"
                  style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                />
                {/* Source handle для исходящих соединений */}
                <Handle
                  type="source"
                  position={Position.Left}
                  id={`${contact.id}-source`}
                  className="schema-source-handle !w-px !h-px !bg-white !border-0"
                  style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                />
              </div>
              <div className="text-xs font-medium text-gray-700 ml-1" style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                {contact.contact_label || `C${contact.contact_number}`}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* TOP contacts - TARGET и SOURCE для устройств сверху */}
        {topContacts.length > 0 && (
          <div className="flex justify-around items-center px-1 py-1 border-b border-gray-200">
            {topContacts.map((contact) => (
              <div key={contact.id} className="flex flex-col items-center relative">
                <div className={`w-3 h-3 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative`}>
                  {/* Target handle для входящих соединений */}
                  <Handle
                    type="target"
                    position={Position.Top}
                    id={`${contact.id}-target`}
                    className="schema-target-handle !w-px !h-px !bg-white !border-0"
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                  {/* Source handle для исходящих соединений */}
                  <Handle
                    type="source"
                    position={Position.Top}
                    id={`${contact.id}-source`}
                    className="schema-source-handle !w-px !h-px !bg-white !border-0"
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                </div>
                <div className="text-xs font-medium text-gray-700 mt-0.5" style={{ fontSize: '8px' }}>
                  {contact.contact_label || `C${contact.contact_number}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Position label (позиционное обозначение) - как у кабелей */}
        <div className={`px-2 py-1 text-center border-b ${deviceColors.borderSecondary}`}>
          <div className={`font-bold ${deviceColors.textPrimary}`} style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {deviceName}
          </div>
          {/* Specification name (название родителя) - под позиционным обозначением */}
          <div className={`font-bold ${deviceColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all', marginTop: '2px' }}>
            {specName}
          </div>
        </div>

        {/* KNX Address - for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
        {(deviceTypeId === 14 || deviceTypeId === 5) && (
          <div className={`px-2 py-1 text-center border-b ${deviceColors.borderSecondary}`}>
            <div className="text-gray-500" style={{ fontSize: '10px' }}>
              {knxAddress || '—'}
            </div>
          </div>
        )}

        {/* Manufacturer and Model - только для устройств (не кабелей) */}
        {!isCable && (manufacturer || model) && (
          <div className={`px-2 py-1 text-center border-b ${deviceColors.borderSecondary} bg-blue-50`}>
            <div className={`font-bold ${deviceColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all' }}>
              {manufacturer && <span>{manufacturer}</span>}
              {manufacturer && model && <span> | </span>}
              {model && <span>{model}</span>}
            </div>
          </div>
        )}

        {/* Device description - всегда отображается для единообразия */}
        <div className="px-1 py-1 text-center border-b border-gray-200" style={{ minHeight: '35px' }}>
          <div className={`font-medium ${deviceColors.textSecondary} flex items-center justify-center h-full`} style={{ fontSize: '10px' }}>
            {description && description.trim() ? description : ''}
          </div>
        </div>

        {/* MAIN SCHEMA: Electrical specifications */}
        <div className={`px-1 py-0.5 text-center border-t border-gray-200 ${deviceColors.bg}`}>
          <div className={`font-medium ${deviceColors.textSecondary}`} style={{ fontSize: '10px' }}>
            {powerConsumption !== undefined && powerConsumption !== null ? 
              `${powerConsumption}Вт` : 
              '—'}
          </div>
          <div className="text-gray-500" style={{ fontSize: '10px' }}>
            {powerConsumption !== undefined && powerConsumption !== null ? 
              `${Math.round(powerConsumption / 220 * 1000)}мА` : 
              '—'}
          </div>
        </div>

        {/* BOTTOM contacts labels - подписи для нижних контактов */}
        {bottomContacts.length > 0 && (
          <div className="flex justify-around items-center px-1 py-1 border-t border-gray-200">
            {bottomContacts.map((contact) => (
              <div key={`label-${contact.id}`} className="flex flex-col items-center">
                <div className="text-xs font-medium text-gray-700" style={{ fontSize: '8px' }}>
                  {contact.contact_label || `C${contact.contact_number}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* BOTTOM contacts - TARGET и SOURCE для устройств снизу (в самом низу ноды) */}
        {bottomContacts.length > 0 && (
          <div className="flex justify-around items-center px-1 py-1">
            {bottomContacts.map((contact) => (
              <div key={contact.id} className="flex flex-col items-center relative">
                <div className={`w-3 h-3 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative`}>
                  {/* Target handle для входящих соединений */}
                  <Handle
                    type="target"
                    position={Position.Bottom}
                    id={`${contact.id}-target`}
                    className="schema-target-handle !w-px !h-px !bg-white !border-0"
                    style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                  />
                  {/* Source handle для исходящих соединений */}
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={`${contact.id}-source`}
                    className="schema-source-handle !w-px !h-px !bg-white !border-0"
                    style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT contacts panel */}
      {rightContacts.length > 0 && (
        <div className="flex flex-col justify-center items-end border-l border-gray-200 py-1 px-1" style={{ width: sidePanelWidth, minHeight: '100%' }}>
          {rightContacts.map((contact) => (
            <div key={contact.id} className="flex items-center relative py-0.5 w-full justify-end">
              <div className="text-xs font-medium text-gray-700 mr-1" style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                {contact.contact_label || `C${contact.contact_number}`}
              </div>
              <div className={`w-3 h-3 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative flex-shrink-0`}>
                {/* Target handle для входящих соединений */}
                <Handle
                  type="target"
                  position={Position.Right}
                  id={`${contact.id}-target`}
                  className="schema-target-handle !w-px !h-px !bg-white !border-0"
                  style={{ right: '50%', transform: 'translate(50%, -50%)' }}
                />
                {/* Source handle для исходящих соединений */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${contact.id}-source`}
                  className="schema-source-handle !w-px !h-px !bg-white !border-0"
                  style={{ right: '50%', transform: 'translate(50%, -50%)' }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}